import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Clock, MapPin, Check, Car, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FAQSection } from '../components/FAQSection';
import { CLEANING_FAQS } from '../lib/faqData';

const LOGO_URL = "/pt-logo.png?v=2";
const CLEANING_IMG = "https://images.pexels.com/photos/6873176/pexels-photo-6873176.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WHATSAPP_LINK = "https://wa.me/message/RYIKLUKDZT2NC1";

// User-provided clean transparent PNG vehicle silhouettes
const VEHICLE_ICONS = {
  sedan: "/vehicle-sedan.png",
  '2_row_suv': "/vehicle-2row-suv.png",
  '3_row_suv': "/vehicle-3row-suv.png",
  minivan: "/vehicle-minivan.png"
};

// Vehicle Icon component with clean transparent PNGs
const VehicleIcon = ({ type, inverted = false }) => {
  const iconUrl = VEHICLE_ICONS[type] || VEHICLE_ICONS.sedan;
  
  return (
    <div 
      className="mx-auto flex items-center justify-center"
      style={{
        width: '70px',
        height: '45px',
      }}
    >
      <img 
        src={iconUrl}
        alt={type}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          filter: inverted ? 'invert(1)' : 'none',
        }}
      />
    </div>
  );
};

// Basic cleaning pricing
const BASIC_CLEANING_PRICES = [
  { size: 'Sedan', price: 85, key: 'sedan' },
  { size: '2 Row SUV', price: 105, key: '2_row_suv' },
  { size: '3 Row SUV', price: 115, key: '3_row_suv' },
  { size: 'Minivan', price: 125, key: 'minivan' }
];

// Full detailing pricing
const FULL_DETAILING_PRICES = [
  { size: 'Sedan', price: 200, key: 'sedan' },
  { size: '2 Row SUV', price: 260, key: '2_row_suv' },
  { size: '3 Row SUV', price: 300, key: '3_row_suv' },
  { size: 'Minivan', price: 350, key: 'minivan' }
];

// What's included in Platinum Package (Interior only)
const BASIC_CLEANING_INCLUDED = {
  interior: ["Vacuuming (seats, carpet, mats)", "Dashboard & Console Wipe", "Cup Holder Cleaning", "Air Freshener", "Window Cleaning (inside)", "Door Panel Wipe Down"]
};

// What's included in Full Detailing
const FULL_DETAILING_INCLUDED = {
  exterior: ["Hand Washing", "Clay Bar Treatment", "Waxing or Sealing", "Trim Restoration", "Window Cleaning"],
  interior: ["Deep Vacuuming", "Carpet & Upholstery Cleaning", "Leather Conditioning", "Dashboard Detailing", "Vent & Crevice Cleaning"]
};

const SERVICES = [
  { type: 'residential', title: 'Platinum Package', desc: 'Pesach cleaning - Chametz free guaranteed', prices: [{ size: 'Sedan', price: 85 }, { size: '2 Row SUV', price: 105 }, { size: '3 Row SUV', price: 115 }, { size: 'Minivan', price: 125 }] },
  { type: 'full_detailing', title: 'Full Detailing', desc: 'Complete interior & exterior detail with waxing, leather conditioning, and more', prices: [{ size: 'Sedan', price: 200 }, { size: '2 Row SUV', price: 260 }, { size: '3 Row SUV', price: 300 }, { size: 'Minivan', price: 350 }] }
];

const FEATURES = [
  { icon: Shield, title: 'Professional Grade', description: 'We use premium products and techniques for a showroom finish' },
  { icon: Clock, title: 'Flexible Scheduling', description: 'Book a time that works for you — we come to your location' },
  { icon: MapPin, title: 'Mobile Service', description: 'We bring the detail shop to your driveway' },
];

export default function CleaningHomePage() {
  const navigate = useNavigate();
  const [clickCount, setClickCount] = useState(0);
  const handleFooterClick = () => {
    const c = clickCount + 1;
    setClickCount(c);
    if (c >= 3) { setClickCount(0); navigate('/cleaning/admin'); }
    setTimeout(() => setClickCount(0), 800);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="cleaning-home-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-xl text-slate-900 font-['Outfit']">Platinum Touch</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/faq" className="text-sm font-medium text-slate-600 hover:text-emerald-500 transition-colors hidden sm:inline" data-testid="cleaning-header-faq-link">FAQ</Link>
              <Button onClick={() => navigate('/cleaning/booking')} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5 py-2 rounded-lg" data-testid="cleaning-book-now-header">
              Book Now
            </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${CLEANING_IMG}')` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-24 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6 animate-fade-in">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Professional Car Cleaning</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 font-['Outfit'] animate-fade-in stagger-1">
              Premium Car<br />
              <span className="text-emerald-400">Cleaning & Detailing</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed animate-fade-in stagger-2">
              Professional mobile detailing brought to your door. From quick washes to full details.
            </p>
            <Button onClick={() => navigate('/cleaning/booking')} className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-10 py-4 rounded-lg shadow-lg text-lg animate-fade-in stagger-3" data-testid="cleaning-hero-book-btn">
              Book Your Cleaning <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Side-by-Side Service Comparison */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white" id="services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Outfit']">Choose Your Service</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Select the level of care your vehicle deserves</p>
          </div>
          
          {/* Comparison Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            
            {/* Platinum Package Card */}
            <div className="bg-white border-2 border-emerald-200 rounded-3xl overflow-hidden hover:border-emerald-400 hover:shadow-2xl transition-all">
              <div className="bg-emerald-50 px-6 py-4 border-b border-emerald-100">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium mb-2">
                      <Sparkles className="w-3 h-3" />
                      Most Popular
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 font-['Outfit']">Platinum Package</h3>
                    <p className="text-slate-500 text-sm">Pesach cleaning - Chametz free guaranteed</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Starting at</p>
                    <p className="text-3xl font-bold text-emerald-500 font-['Outfit']">$85</p>
                  </div>
                </div>
              </div>
              
              {/* Vehicle Pricing Grid */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {BASIC_CLEANING_PRICES.map((item) => (
                    <div 
                      key={item.key}
                      className="bg-slate-50 rounded-xl p-4 text-center hover:bg-emerald-50 transition-colors"
                    >
                      <VehicleIcon type={item.key} />
                      <p className="font-medium text-slate-700 text-sm mt-2">{item.size}</p>
                      <p className="text-xl font-bold text-emerald-600 font-['Outfit']">${item.price}</p>
                    </div>
                  ))}
                </div>
                
                {/* What's Included */}
                <div className="border-t border-slate-100 pt-6">
                  <h4 className="font-semibold text-slate-900 mb-4 text-sm uppercase tracking-wide">What's Included</h4>
                  <div>
                    <p className="text-xs font-semibold text-emerald-600 mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Interior Cleaning</p>
                    <div className="grid grid-cols-2 gap-x-4">
                      {BASIC_CLEANING_INCLUDED.interior.map(item => (
                        <p key={item} className="text-xs text-slate-600 flex items-center gap-1.5 py-0.5">
                          <Check className="w-3 h-3 text-emerald-500" />{item}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={() => navigate('/cleaning/booking?type=residential')} 
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 rounded-xl mt-6"
                  data-testid="basic-cleaning-book-btn"
                >
                  Book Platinum Package <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Full Detailing Card */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-400/30 rounded-3xl overflow-hidden hover:border-amber-400 hover:shadow-2xl transition-all">
              <div className="bg-amber-500/10 px-6 py-4 border-b border-amber-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-xs font-medium mb-2">
                      <Shield className="w-3 h-3" />
                      Premium Service
                    </div>
                    <h3 className="text-2xl font-bold text-white font-['Outfit']">Full Detailing</h3>
                    <p className="text-slate-400 text-sm">Complete detail with waxing & conditioning</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Starting at</p>
                    <p className="text-3xl font-bold text-amber-400 font-['Outfit']">$200</p>
                  </div>
                </div>
              </div>
              
              {/* Vehicle Pricing Grid */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {FULL_DETAILING_PRICES.map((item) => (
                    <div 
                      key={item.key}
                      className="bg-slate-800/50 rounded-xl p-4 text-center hover:bg-slate-700/50 transition-colors"
                    >
                      <VehicleIcon type={item.key} inverted={true} />
                      <p className="font-medium text-slate-300 text-sm mt-2">{item.size}</p>
                      <p className="text-xl font-bold text-amber-400 font-['Outfit']">${item.price}</p>
                    </div>
                  ))}
                </div>
                
                {/* What's Included */}
                <div className="border-t border-slate-700 pt-6">
                  <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">What's Included</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1"><Car className="w-3 h-3" /> Exterior</p>
                      {FULL_DETAILING_INCLUDED.exterior.map(item => (
                        <p key={item} className="text-xs text-slate-400 flex items-center gap-1.5 py-0.5">
                          <Check className="w-3 h-3 text-amber-500" />{item}
                        </p>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Interior</p>
                      {FULL_DETAILING_INCLUDED.interior.map(item => (
                        <p key={item} className="text-xs text-slate-400 flex items-center gap-1.5 py-0.5">
                          <Check className="w-3 h-3 text-amber-500" />{item}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={() => navigate('/cleaning/booking?type=full_detailing')} 
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3 rounded-xl mt-6"
                  data-testid="full-detailing-book-btn"
                >
                  Book Full Detailing <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {FEATURES.map(f => (
              <div key={f.title} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <f.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 font-['Outfit']">{f.title}</h3>
                <p className="text-slate-500 text-sm">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        faqs={CLEANING_FAQS}
        accentColor="#10B981"
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about our car cleaning services"
        sectionId="cleaning-faq"
      />

      {/* CTA */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Outfit']">Ready for a Spotless Ride?</h2>
          <p className="text-emerald-100 text-lg mb-8">Book your professional car cleaning today</p>
          <Button onClick={() => navigate('/cleaning/booking')} className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-10 py-4 rounded-lg text-lg" data-testid="cleaning-cta-book-btn">
            Book Now <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm cursor-default select-none" onClick={handleFooterClick}>&copy; {new Date().getFullYear()} Platinum Touch. All rights reserved.</p>
        </div>
      </footer>

      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 flex items-center gap-2 group"
        data-testid="cleaning-whatsapp-button"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap font-medium text-sm">
          Chat with us
        </span>
      </a>
    </div>
  );
}
